import React, {useState, useRef, useContext, useEffect, useMemo} from "react";
import {jsPDF} from "jspdf";
import "jspdf-autotable";
import html2canvas from "html2canvas";
import SignatureCanvas from "react-signature-canvas";
import {FormDataContext} from "./FormDataContext";
import armenianFontBase64 from "../Fonts/armenianFontBase64";
import {debounce} from "lodash";
import {useNavigate} from "react-router-dom";
import {APIService} from "../api";
import {COURSE_PLAN_LABEL, findCoursePlan, getCoursePlan, isSafeHttpUrl} from "../utilities/coursePlan";
import CoursePlanLink from "./CoursePlanLink";

import logo from "../assets/Logo.png";
import logo2 from "../assets/Kniq.png";
import logo3 from "../assets/Logo2.png";
import st from "../assets/st.png";

import "../Style/Page5.css";

const Page5 = () => {
    const benefitRef = useRef();
    const signatureRef = useRef(null);

    const {formData} = useContext(FormDataContext);
    const navigate = useNavigate();

    const [currentDate] = useState(new Date().toLocaleDateString("hy-AM"));
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSigned, setIsSigned] = useState(false);

    const [pdfData, setPdfData] = useState(null);
    const [pdfObjectUrl, setPdfObjectUrl] = useState(null);

    const [isLoading, setIsLoading] = useState(false);
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5002";

    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [randomId, setRandomId] = useState(null);

    // Course plan link, resolved from selectedCourse against the course
    // catalog (product.course_plan). The backend resolves it again on save
    // and never trusts this client-side value.
    const [coursePlan, setCoursePlan] = useState("");
    const [coursePlanReady, setCoursePlanReady] = useState(false);

    // ---- REQUIRED FIELDS ----
    const REQUIRED_FIELDS = useMemo(
        () => ["firstName", "lastName", "phone", "email", "selectedCourse"],
        []
    );

    const FIELD_LABELS = useMemo(
        () => ({
            firstName: "Անուն",
            lastName: "Ազգանուն",
            phone: "Հեռախոսահամար",
            email: "Էլ․ փոստ",
            selectedCourse: "Դասընթացի անվանում",
        }),
        []
    );

    const validateRequiredFields = (data) => {
        const missing = [];
        for (const key of REQUIRED_FIELDS) {
            const value = String(data?.[key] ?? "").trim();
            if (!value) missing.push(key);
        }
        return {ok: missing.length === 0, missing};
    };

    const showMissingFieldsAlert = (missingKeys) => {
        const text = missingKeys.map((k) => FIELD_LABELS[k] || k).join(", ");
        alert("❌ Խնդրում ենք լրացնել պարտադիր դաշտերը՝\n" + text);
    };

    const isFormReady = useMemo(() => {
        return validateRequiredFields(formData).ok;
    }, [formData]);

    // ---- Resolve course plan link from selectedCourse ----
    const selectedCourse = formData.selectedCourse;
    const storedCoursePlan = getCoursePlan(formData);
    useEffect(() => {
        let cancelled = false;
        const fallback = storedCoursePlan;

        setCoursePlanReady(false);
        APIService.getProducts()
            .then((products) => {
                if (cancelled) return;
                const resolved = Array.isArray(products)
                    ? findCoursePlan(products, selectedCourse)
                    : (isSafeHttpUrl(fallback) ? fallback : "");
                setCoursePlan(resolved);
            })
            .catch((error) => {
                if (cancelled) return;
                console.error("❌ Դասընթացի պլանի հղումը չհաջողվեց ստանալ:", error);
                setCoursePlan(isSafeHttpUrl(fallback) ? fallback : "");
            })
            .finally(() => {
                if (!cancelled) setCoursePlanReady(true);
            });

        return () => {
            cancelled = true;
        };
    }, [selectedCourse, storedCoursePlan]);

    const pdfInput = useMemo(() => ({...formData, coursePlan}), [formData, coursePlan]);

    // ---- Cleanup object URL ----
    useEffect(() => {
        return () => {
            if (pdfObjectUrl) {
                try {
                    URL.revokeObjectURL(pdfObjectUrl);
                } catch (_) {
                }
            }
        };
    }, [pdfObjectUrl]);

    // ---- Mobile detect ----
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        const existingId = sessionStorage.getItem("randomId");
        if (existingId) {
            setRandomId(Number(existingId));
            return;
        }

        const randomBuffer = new Uint32Array(1);
        window.crypto.getRandomValues(randomBuffer);
        const generatedId = 100000 + (randomBuffer[0] % 900000); // 6-digit id
        setRandomId(generatedId);
        sessionStorage.setItem("randomId", String(generatedId));
    }, []);

    const isIOS = (() => {
        const ua = navigator.userAgent || "";
        const iOS = /iPad|iPhone|iPod/.test(ua);
        const iPadOS13Plus = ua.includes("Mac") && "ontouchend" in document;
        return iOS || iPadOS13Plus;
    })();

    const isInAppBrowser = () => {
        const ua = navigator.userAgent || "";
        return /Instagram|FBAN|FBAV|Messenger|TikTok|Line|WeChat|Twitter/i.test(ua);
    };

    const supportsAbortController = typeof AbortController !== "undefined";

    const blobToBase64 = (blob) =>
        new Promise((resolve, reject) => {
            try {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            } catch (e) {
                reject(e);
            }
        });

    const generatePDF = async (
        data,
        includeSignature = false,
        signatureImage = null,
        secondLastColumnWidth = 80
    ) => {
        try {
            if (!data || typeof data !== "object") {
                console.error("❌ generatePDF: data is invalid");
                return null;
            }

            // HARD STOP: do not generate if required fields missing
            const check = validateRequiredFields(data);
            if (!check.ok) {
                console.log("⛔ PDF not generated (missing required fields):", check.missing);
                return null;
            }

            const doc = new jsPDF({
                orientation: "p",
                unit: "mm",
                format: "a4",
                putOnlyUsedFonts: true,
                floatPrecision: 16,
            });

            // Font setup with fallback
            let fontName = "helvetica";
            try {
                if (typeof armenianFontBase64 !== "undefined" && armenianFontBase64) {
                    doc.addFileToVFS("NotoSansArmenian-Regular.ttf", armenianFontBase64);
                    doc.addFont("NotoSansArmenian-Regular.ttf", "NotoSans", "normal");
                    doc.setFont("NotoSans", "normal");
                    fontName = "NotoSans";
                } else {
                    doc.setFont("helvetica", "normal");
                }
            } catch (e) {
                console.warn("⚠️ Font setup failed, using helvetica:", e);
                doc.setFont("helvetica", "normal");
            }
            const otherStudentValue =
                data.other_student || data.otherStudent || "";

            const papaymentMethod = (() => {
                if (data.paymentMethod === "full") return "Միանվագ";
                if (data.paymentMethod === "monthly") return "Մաս մաս՝ ամսական վճարումներ կատարելով մինչև դասընթացի ավարտը";
                if (data.paymentMethod === "paylater")
                    return "Հետվճարային՝ RocketLine / PayLater / PartPay / AmeriaPay";
                return data.paymentMethod || "";
            })();

            const coursePlanUrl = isSafeHttpUrl(data.coursePlan) ? data.coursePlan : "";

            const titleInfo = [
                [
                    " ",
                    `Պայմանագրի կողմ \n«Սովորիր Այթի» ՍՊԸ \nԿապ  +374 44 240 202 info@sovorir.it`,
                ],

                ["Անձնական տվյալներ", ""],
                ["Անուն", data.firstName || ""],
                ["Ազգանուն", data.lastName || ""],
                ["Հայրանուն", data.patronymic || ""],
                ["Անձնագրի սերիա", data.passportSeries || ""],
                ["ՀԾՀ - հանրային ծառայությունների համարանիշը", data.htsh || ""],
                ["Տրված է", data.issuedBy || ""],
                ["Գրանցման հասցե", data.residence || ""],
                ["Էլ․ փոստ", data.email || ""],
                ["Հեռախոսահամար", data.phone || ""],

                ["Դասընթացի մանրամասները", ""],
                ["Դասընթացի անվանում", data.selectedCourse || ""],
                ...(coursePlanUrl ? [[COURSE_PLAN_LABEL, coursePlanUrl]] : []),
                [
                    "Ցանկանում եմ օգտվել սոցվճարի հետվերադարձից",
                    data.hasDiscount1 ? "Այո" : "",
                ],
                ["Ում համար եմ վճարել, ուսանողի անուն ազգանուն։", otherStudentValue],
                ["Վճարման եղանակը", papaymentMethod],
                ["Ընդհանուր գումարը", `${data.originalPrice || data.totalPrice || ""} ֏`],

                ...(data.hasDiscount && data.discount
                    ? [["Սահմանել վերջնական գինը", `${data.discount} ֏`]]
                    : []),
                ["Պայմանագրի կնքման ամսաթիվ", currentDate],

                [
                    "«Սովորիր Այթի» սահմանափակ պատասխանատվությամբ ընկերություն  ",
                    `Ծանոթացել եմ և համաձայն եմ  https://sovorir.it  կայքէջում հրապարակված վճարովի ծառայությունների մատուցման հրապարակային պայմանագրին և հավաստիացնում եմ մուտքագրված տվյալների ճիշտ են։`,
                ],
                [
                    "",
                    `Ուսանողի ԱԱՀ \n${data.firstName || ""}  ${
                        data.lastName || ""
                    } ${data.patronymic || ""}\n\n \n\n`,
                ],
            ];

            let yPosition = 10;
            const lastColumnWidth = 100;

            // Row indexes are looked up by label so the optional course plan
            // row doesn't shift the stamp onto the wrong row.
            const paymentRowIndex = titleInfo.findIndex((row) => row[0] === "Վճարման եղանակը");
            const lastRowIndex = titleInfo.length - 1;
            let lastRowCell = null;
            let paymentRowCell = null;

            doc.autoTable({
                startY: yPosition,
                body: titleInfo,
                theme: "plain",
                // On overflow, move a whole row to the next page instead of
                // splitting it (keeps the signature row intact on one page).
                rowPageBreak: "avoid",
                styles: {
                    fontSize: 10,
                    font: fontName,
                    cellPadding: 3,
                    lineWidth: 0.1,
                    lineColor: [0, 0, 0],
                    minCellHeight: 2,
                },
                columnStyles: {
                    0: {cellWidth: secondLastColumnWidth, fontStyle: "bold"},
                    1: {cellWidth: lastColumnWidth},
                },
                didParseCell: function (tableData) {
                    const highlightedTitles = ["Անձնական տվյալներ", "Դասընթացի մանրամասները"];

                    if (highlightedTitles.includes(tableData.cell.raw)) {
                        tableData.cell.styles.fillColor = [186, 184, 184];
                        tableData.cell.styles.textColor = [0, 0, 0];
                        tableData.cell.colSpan = 2;
                        tableData.cell.styles.halign = "center";
                        tableData.cell.styles.fontStyle = "bold";
                    }

                    tableData.cell.styles.minCellHeight = 10;

                    // Compact row (smaller URL font, tighter padding) so the extra
                    // row still keeps the whole contract on a single A4 page.
                    if (tableData.row.raw?.[0] === COURSE_PLAN_LABEL) {
                        tableData.cell.styles.minCellHeight = 6;
                        tableData.cell.styles.cellPadding = {top: 1.5, bottom: 1.5, left: 3, right: 3};
                        if (tableData.column.index === 1) {
                            tableData.cell.styles.fontSize = 8;
                            tableData.cell.styles.textColor = [0, 0, 238];
                        }
                    }
                },
                didDrawCell: function (tableData) {
                    if (tableData.column.index === 0 && tableData.row.index === 0 && logo) {
                        doc.addImage(logo, "PNG", tableData.cell.x + 8, tableData.cell.y + 4, 50, 10);
                    }

                    // Clickable course plan: a link annotation over the whole value cell.
                    if (
                        coursePlanUrl &&
                        tableData.column.index === 1 &&
                        tableData.row.raw?.[0] === COURSE_PLAN_LABEL
                    ) {
                        doc.link(
                            tableData.cell.x,
                            tableData.cell.y,
                            tableData.cell.width,
                            tableData.cell.height,
                            {url: coursePlanUrl}
                        );
                    }

                    if (tableData.column.index === 0 && tableData.row.index === lastRowIndex) {
                        lastRowCell = {x: tableData.cell.x, y: tableData.cell.y, page: tableData.pageNumber};
                    }

                    if (tableData.column.index === 0 && tableData.row.index === lastRowIndex && logo2) {
                        doc.addImage(logo2, "PNG", tableData.cell.x + 10, tableData.cell.y + 2, 25, 25);
                    }

                    if (tableData.column.index === 0 && tableData.row.index === paymentRowIndex) {
                        paymentRowCell = {
                            x: tableData.cell.x,
                            y: tableData.cell.y,
                            height: tableData.cell.height,
                            page: tableData.pageNumber,
                        };
                    }
                },
            });

            // Stamp: 60mm below the "Վճարման եղանակը" row, which lands it inside
            // the last row (≈13.6mm from its top). If the table overflowed and
            // that row ended up on a different page than the last row (or wasn't
            // found), use the same spot inside the last row instead of drawing
            // off the bottom of the page.
            if (st) {
                const STAMP_OFFSET_IN_LAST_ROW = 13.6;
                if (paymentRowCell && (!lastRowCell || paymentRowCell.page === lastRowCell.page)) {
                    doc.setPage(paymentRowCell.page);
                    doc.addImage(st, "PNG", paymentRowCell.x + 35, paymentRowCell.y + paymentRowCell.height + 49, 40, 20);
                } else if (lastRowCell) {
                    doc.setPage(lastRowCell.page);
                    doc.addImage(st, "PNG", lastRowCell.x + 35, lastRowCell.y + STAMP_OFFSET_IN_LAST_ROW, 40, 20);
                }
            }

            // signature (if requested)
            // Anchored to the last ("Ուսանողի ԱԱՀ") row: with the default layout
            // that row starts at ~241.7mm, i.e. exactly the previous fixed y=260.
            // The anchor keeps the signature inside that row when the optional
            // course plan row pushes it down.
            if (includeSignature && signatureImage) {
                const studentAahXPosition = 130;
                const SIGNATURE_OFFSET_IN_LAST_ROW = 18.3;
                let studentAahYPosition = 260;
                if (lastRowCell) {
                    doc.setPage(lastRowCell.page);
                    studentAahYPosition = lastRowCell.y + SIGNATURE_OFFSET_IN_LAST_ROW;
                }
                doc.addImage(signatureImage, "PNG", studentAahXPosition, studentAahYPosition, 30, 10);
            }

            const pdfBlob = doc.output("blob");

            // Safety: avoid saving/using tiny PDF
            if (!pdfBlob || pdfBlob.size < 5000) {
                console.error("❌ PDF blob too small:", pdfBlob?.size);
                alert("❌ PDF-ը սխալ է գեներացվել։ Փորձեք կրկին։");
                return null;
            }

            const file = new File([pdfBlob], "agreement.pdf", {type: "application/pdf"});

            if (pdfObjectUrl) {
                try {
                    URL.revokeObjectURL(pdfObjectUrl);
                } catch (_) {
                }
            }

            const objectUrl = URL.createObjectURL(file);
            setPdfObjectUrl(objectUrl);
            setPdfData(file);

            return file;
        } catch (error) {
            console.error("❌ PDF գեներացման սխալ:", error);
            return null;
        }
    };

    // Generate PDF only when form is ready
    useEffect(() => {
        const debouncedGenerate = debounce(() => {
            if (!isFormReady || !coursePlanReady) return;
            generatePDF(pdfInput, false);
        }, 300);

        debouncedGenerate();
        return () => debouncedGenerate.cancel();
    }, [pdfInput, isFormReady, coursePlanReady]);

    // ---- Benefit card image ----
    const getBenefitImageBlob = async () => {
        const ref = benefitRef?.current;
        if (!ref) return null;

        try {
            const images = ref.querySelectorAll("img");
            await Promise.all(
                Array.from(images).map((img) =>
                    img.complete
                        ? Promise.resolve()
                        : new Promise((resolve) => {
                            img.onload = resolve;
                            img.onerror = resolve;
                        })
                )
            );

            const canvas = await html2canvas(ref, {
                useCORS: true,
                allowTaint: false,
                backgroundColor: null,
                scale: Math.min(window.devicePixelRatio || 2, 2),
                logging: false,
                width: ref.offsetWidth,
                height: ref.offsetHeight,
                foreignObjectRendering: false,
                imageTimeout: 15000,
                removeContainer: true,
                onclone: (clonedDoc) => {
                    try {
                        clonedDoc.querySelectorAll("img").forEach((img) => {
                            img.crossOrigin = "anonymous";
                        });
                    } catch (_) {
                    }
                },
            });

            return await new Promise((resolve) => {
                canvas.toBlob(
                    (blob) => resolve(blob || null),
                    "image/png",
                    0.8
                );
            });
        } catch (error) {
            console.error("❌ Քարտի նկարը ստեղծելու սխալ:", error);
            return null;
        }
    };
    const handleClearSignature = () => {
        signatureRef.current?.clear();
        setIsSigned(false);
    };

    // ---- Confirm signature ----
    const handleConfirmSignature = async () => {
        if (!signatureRef.current) {
            alert("Ստորագրության դաշտը չի գտնվել։");
            return;
        }

        // required fields check before signing
        const check = validateRequiredFields(formData);
        if (!check.ok) {
            showMissingFieldsAlert(check.missing);
            return;
        }

        const canvas = signatureRef.current.getCanvas();
        const ctx = canvas.getContext("2d");
        const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
        const hasInk = pixels.some((value) => value !== 0);

        if (!hasInk) {
            alert("Խնդրում ենք ավելացնել ստորագրությունը");
            return;
        }

        try {
            const signatureDataUrl = signatureRef.current.toDataURL("image/png");

            // regenerate full PDF with signature
            const signedFile = await generatePDF(pdfInput, true, signatureDataUrl);
            if (!signedFile) return;

            setIsModalOpen(false);
            setIsSigned(true);
        } catch (error) {
            console.error("❌ Ստորագրությունը մշակելու սխալ:", error);
            alert("Ստորագրությունը մշակելու ընթացքում սխալ տեղի ունեցավ։");
        }
    };

    // ---- Submit ----
    const handleSubmitClick = async () => {
        if (!isSigned) {
            alert("Խնդրում ենք ստորագրել փաստաթուղթը։");
            return;
        }

        const check = validateRequiredFields(formData);
        if (!check.ok) {
            showMissingFieldsAlert(check.missing);
            return;
        }

        if (!pdfData || pdfData.size < 5000) {
            alert("❌ PDF-ը դեռ պատրաստ չէ կամ դատարկ է։ Խնդրում ենք փորձել կրկին։");
            return;
        }

        await sendPDF();
    };

    // ---- Send PDF ----
    const sendPDF = async () => {
        if (isInAppBrowser()) {
            alert("⚠️ Խնդրում ենք բացել այս էջը Safari կամ Chrome բրաուզերով, ոչ թե հավելվածի ներսում։");
            return;
        }

        const check = validateRequiredFields(formData);
        if (!check.ok) {
            showMissingFieldsAlert(check.missing);
            return;
        }

        if (!pdfData || pdfData.size < 5000) {
            alert("❌ PDF-ը դատարկ է կամ սխալ է գեներացվել։");
            return;
        }

        setIsLoading(true);

        const controller = supportsAbortController ? new AbortController() : null;
        const timeoutId = supportsAbortController
            ? setTimeout(() => controller.abort(), 60000)
            : null;

        try {
            const dataToSend = new FormData();
            dataToSend.append("file", pdfData, "agreement.pdf");
            const benefitImageBlob = await getBenefitImageBlob();

            if (benefitImageBlob) {
                dataToSend.append(
                    "cardImage",
                    benefitImageBlob,
                    `card_${randomId}.png`
                );
            }
            const KNOWN_FIELDS = {
                citizenship: 255,
                firstName: 255,
                lastName: 255,
                patronymic: 255,
                passportSeries: 50,
                htsh: 255,
                issuedBy: 255,
                residence: 255,
                phone: 50,
                email: 255,
                confirmEmail: 255,
                selectedCourse: 255,
                paymentMethod: 50,
                other_student: 255,
                discount: null,
                hasDiscount: null,
                hasDiscount1: null,
                totalPrice: null,
                originalPrice: null,
                selectedDate: null,
            };

            Object.entries(KNOWN_FIELDS).forEach(([key, maxLen]) => {
                const value = (formData || {})[key];
                if (value === undefined || value === null) return;
                let serialized = typeof value === "object" ? JSON.stringify(value) : String(value);
                // Enforce column length limits client-side as an extra safety net
                if (maxLen && serialized.length > maxLen) {
                    serialized = serialized.slice(0, maxLen);
                }
                dataToSend.append(key, serialized);
            });

            dataToSend.append("cart_id", String(randomId));

            const fetchOptions = {
                method: "POST",
                body: dataToSend,
                headers: {Accept: "application/json"},
            };

            if (supportsAbortController) {
                fetchOptions.signal = controller.signal;
            }

            const response = await fetch(`${API_URL}/pdf/save`, fetchOptions);
            const result = await response.json().catch(() => ({}));

            if (!response.ok) {
                throw new Error(result?.message || "Սխալ տեղի ունեցավ PDF-ը պահպանելիս։");
            }

            alert(
                "Սիրելի ուսանող,\n\nՏեղեկացնում ենք, որ Ձեզ հետ կնքված հրապարակային պայմանագիրը, կազմակերպության հրապարակային պայմանները և Ձեր Benefit Card-ը հաստատվելուց հետո ուղարկվելու է Ձեր էլեկտրոնային հասցեին։\n\n📩 Շնորհակալ ենք վստահության համար։\nՍովորիր.Այթի թիմ"
            );

            setTimeout(() => {
                window.location.href = "https://www.instagram.com/sovorir.it";
            }, 1000);
        } catch (error) {
            console.error("❌ Սխալ sendPDF-ի ընթացքում:", error);

            const getUserFriendlyError = (err) => {
                if (err?.name === "AbortError") return "Հարցումը չափազանց երկար տևեց։ Փորձեք կրկին։";
                const msg = String(err?.message || "");
                if (msg.includes("Failed to fetch")) return "Ցանցային կապի խնդիր։ Ստուգեք ինտերնետը։";
                if (msg.includes("413")) return "Ֆայլը չափազանց մեծ է։ Փորձեք փոքրացնել ֆայլը։";
                return msg || "Անհայտ սխալ տեղի ունեցավ։";
            };

            alert("PDF պահպանման սխալ:\n" + getUserFriendlyError(error));
        } finally {
            if (timeoutId) clearTimeout(timeoutId);
            setIsLoading(false);
        }
    };

    const handleback = () => navigate(-1);

    const openPDF = () => {
        if (!pdfObjectUrl) {
            alert("PDF-ը դեռ պատրաստ չէ։");
            return;
        }

        try {
            const isAndroid = /Android/.test(navigator.userAgent);

            if (isIOS) {
                window.location.href = pdfObjectUrl;
            } else if (isAndroid) {
                const link = document.createElement("a");
                link.href = pdfObjectUrl;
                link.target = "_blank";
                link.download = "agreement.pdf";
                document.body.appendChild(link);
                link.click();
                setTimeout(() => document.body.removeChild(link), 100);
            } else {
                const link = document.createElement("a");
                link.href = pdfObjectUrl;
                link.target = "_blank";
                link.rel = "noopener noreferrer";
                document.body.appendChild(link);
                link.click();
                setTimeout(() => document.body.removeChild(link), 100);
            }
        } catch (error) {
            console.error("❌ PDF բացելու սխալ:", error);
            alert("PDF բացելու սխալ։ Խնդրում ենք փորձել կրկին։");
        }
    };

    return (
        <div className="page5">
            <div className="containerp5">
                <h1>Ծառայությունների մատուցման դրույթներ</h1>

                <div>
                    {pdfData && (
                        isMobile ? (
                            <button className="submit-btn btm2" onClick={openPDF}>
                                Տեսնել պայմանագիրը
                            </button>
                        ) : (
                            pdfObjectUrl ? (
                                <iframe
                                    src={`${pdfObjectUrl}#toolbar=0`}
                                    className="iframe"
                                    title="PDF պայմանագիր"
                                    onError={(e) => {
                                        console.error("iframe սխալ:", e);
                                        e.target.style.display = "none";
                                        document.getElementById("pdf-fallback-btn")?.click();
                                    }}
                                />
                            ) : (
                                <div className="loading-pdf">Բեռնվում է PDF-ը...</div>
                            )
                        )
                    )}

                    <button
                        id="pdf-fallback-btn"
                        style={{display: "none"}}
                        onClick={openPDF}
                    >
                        Տեսնել պայմանագիրը
                    </button>

                    {coursePlan && (
                        <p className="course-plan-link">
                            <strong>{COURSE_PLAN_LABEL}՝ </strong>
                            <CoursePlanLink href={coursePlan}>Դիտել դասընթացի պլանը</CoursePlanLink>
                        </p>
                    )}
                </div>

                <button
                    onClick={() => {
                        const check = validateRequiredFields(formData);
                        if (!check.ok) {
                            showMissingFieldsAlert(check.missing);
                            return;
                        }
                        if (!coursePlanReady) {
                            alert("PDF-ը դեռ պատրաստվում է։ Խնդրում ենք մի պահ սպասել։");
                            return;
                        }
                        setIsModalOpen(true);
                    }}
                    className="submit-btn submit-btn1"
                >
                    Ստորագրել պայմանագիրը
                </button>

                <div className="benefit-section">
                    <div ref={benefitRef} className="benefit-card">
                        <div className="logo-area">
                            <img src={logo3} alt="Logo" className="card-logo" crossOrigin="anonymous"/>
                        </div>

                        <h2 className="card-name">
                            {formData.firstName} {formData.lastName}
                        </h2>

                        <p className="card-subtitle">ՈՒՍԱՆՈՂԱԿԱՆ ՔԱՐՏ</p>
                        <p className="card-id">
                            <span>Քարտի համարը</span> {randomId}
                        </p>

                        <p className="card-course">
                            <span>Դասընթաց </span> {formData.selectedCourse}
                        </p>

                        <p className="card-phone">044 24 02 02</p>
                        <div className="card-footer"/>
                    </div>
                </div>

                <div className="btn_b_n">
                    <button type="button" className="submit-btn" onClick={handleback}>
                        Նախորդ էջ
                    </button>

                    <button
                        onClick={handleSubmitClick}
                        className={`submit-btn ${(isSigned && !isLoading) ? "" : "disabled-btn"}`}
                        disabled={!isSigned || isLoading}
                    >
                        {isLoading ? "Վերբեռնվում է" : "Ուղարկել հաստատման"}
                    </button>
                </div>

                {isModalOpen && (
                    <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
                        <div className="modal" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-content">
                                <h2>Ստորագրեք այստեղ</h2>

                                <SignatureCanvas
                                    ref={signatureRef}
                                    penColor="black"
                                    canvasProps={{
                                        className: "signature-canvas",
                                        width: 300,
                                        height: 150,
                                    }}
                                />

                                <div className="flex justify-end gap-4 mt-4">
                                    <button onClick={handleClearSignature} className="bg-red">
                                        Մաքրել ստորագրությունը
                                    </button>

                                    <button onClick={handleConfirmSignature} className="submit-btn">
                                        Հաստատել
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Page5;
